import React, { useState } from 'react';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Download, Eye, CheckCircle, AlertCircle,
  User, Briefcase, GraduationCap, Languages, Award,
  Phone, Mail, MapPin, Calendar, Plus, Trash2, Save, X
} from 'lucide-react';

// Örnek CV verisi
const sampleCVData = {
  personalInfo: {
    fullName: 'Elif Kaya',
    email: 'elif.kaya@email.com',
    phone: '+90 532 123 45 67',
    birthDate: '1998-05-15',
    nationality: 'T.C.',
    address: 'Kadıköy, İstanbul'
  },
  physicalInfo: {
    height: '168',
    weight: '58',
    eyeColor: 'Kahverengi',
    hairColor: 'Koyu Kahve',
    shoeSize: '38'
  },
  education: [
    { school: 'İstanbul Üniversitesi', degree: 'Lisans', field: 'Turizm İşletmeciliği', startYear: '2016', endYear: '2020' },
    { school: 'THK Üniversitesi', degree: 'Sertifika', field: 'Kabin Memurluğu Eğitimi', startYear: '2020', endYear: '2020' }
  ],
  experience: [
    { company: 'Hilton İstanbul', position: 'Misafir İlişkileri Uzmanı', startDate: '2020-06', endDate: '2023-08', description: 'VIP misafir karşılama, şikayet yönetimi, check-in/check-out işlemleri' },
    { company: 'Starbucks', position: 'Vardiya Sorumlusu', startDate: '2018-09', endDate: '2020-05', description: 'Ekip yönetimi, müşteri hizmetleri, stok kontrolü' }
  ],
  languages: [
    { language: 'Türkçe', level: 'Ana Dil' },
    { language: 'İngilizce', level: 'C1' },
    { language: 'Almanca', level: 'A2' }
  ],
  skills: ['Müşteri Hizmetleri', 'İletişim', 'Takım Çalışması', 'Stres Yönetimi', 'İlk Yardım', 'Problem Çözme'],
  certificates: ['İlk Yardım Sertifikası (2023)', 'IATA Kabin Ekibi Eğitimi', 'İş Güvenliği Sertifikası']
};

export default function CabinCrewCareerCV() {
  const [cvData, setCvData] = useState({
    personalInfo: {
      fullName: '',
      email: '',
      phone: '',
      birthDate: '',
      nationality: '',
      address: '',
      photo: null
    },
    physicalInfo: {
      height: '',
      weight: '',
      eyeColor: '',
      hairColor: '',
      shoeSize: ''
    },
    education: [
      { school: '', degree: '', field: '', startYear: '', endYear: '' }
    ],
    experience: [
      { company: '', position: '', startDate: '', endDate: '', description: '' }
    ],
    languages: [
      { language: 'Türkçe', level: 'Ana Dil' },
      { language: 'İngilizce', level: '' }
    ],
    skills: [],
    certificates: []
  });

  const [activeSection, setActiveSection] = useState('personal');
  const [previewMode, setPreviewMode] = useState(false);
  const [showSampleCV, setShowSampleCV] = useState(false);

  // Profesyonel CV Önizleme Komponenti
  const CVPreview = ({ data, isSample = false }) => (
    <div className="bg-white rounded-xl shadow-2xl overflow-hidden max-w-3xl mx-auto">
      {/* Header - Professional Style */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-8">
        <div className="flex items-start gap-6">
          <div className="w-28 h-28 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center text-4xl font-bold shadow-lg">
            {data.personalInfo.fullName?.split(' ').map(n => n[0]).join('') || 'CV'}
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">{data.personalInfo.fullName || 'Ad Soyad'}</h1>
            <p className="text-slate-300 text-lg mb-4">Kabin Memuru Adayı</p>
            <div className="flex flex-wrap gap-4 text-sm">
              {data.personalInfo.email && (
                <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{data.personalInfo.email}</span>
              )}
              {data.personalInfo.phone && (
                <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{data.personalInfo.phone}</span>
              )}
              {data.personalInfo.address && (
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{data.personalInfo.address}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* Physical Info */}
        {(data.physicalInfo.height || data.physicalInfo.weight) && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 border-b-2 border-rose-400 pb-2 mb-4">Fiziksel Özellikler</h2>
            <div className="flex flex-wrap gap-4">
              {data.physicalInfo.height && <Badge variant="outline" className="px-3 py-1">Boy: {data.physicalInfo.height} cm</Badge>}
              {data.physicalInfo.weight && <Badge variant="outline" className="px-3 py-1">Kilo: {data.physicalInfo.weight} kg</Badge>}
              {data.physicalInfo.eyeColor && <Badge variant="outline" className="px-3 py-1">Göz: {data.physicalInfo.eyeColor}</Badge>}
              {data.physicalInfo.hairColor && <Badge variant="outline" className="px-3 py-1">Saç: {data.physicalInfo.hairColor}</Badge>}
            </div>
          </div>
        )}

        {/* Education */}
        {data.education?.length > 0 && data.education[0].school && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 border-b-2 border-rose-400 pb-2 mb-4 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-rose-500" /> Eğitim
            </h2>
            <div className="space-y-3">
              {data.education.filter(e => e.school).map((edu, i) => (
                <div key={i} className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-slate-800">{edu.school}</p>
                    <p className="text-slate-600 text-sm">{edu.degree} - {edu.field}</p>
                  </div>
                  <span className="text-sm text-slate-500">{edu.startYear} - {edu.endYear}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Experience */}
        {data.experience?.length > 0 && data.experience[0].company && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 border-b-2 border-rose-400 pb-2 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-rose-500" /> İş Deneyimi
            </h2>
            <div className="space-y-4">
              {data.experience.filter(e => e.company).map((exp, i) => (
                <div key={i}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-slate-800">{exp.position}</p>
                      <p className="text-rose-600 text-sm">{exp.company}</p>
                    </div>
                    <span className="text-sm text-slate-500">{exp.startDate} - {exp.endDate || 'Devam'}</span>
                  </div>
                  {exp.description && <p className="text-slate-600 text-sm mt-1">{exp.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Languages */}
        {data.languages?.length > 0 && data.languages[0].level && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 border-b-2 border-rose-400 pb-2 mb-4 flex items-center gap-2">
              <Languages className="w-5 h-5 text-rose-500" /> Diller
            </h2>
            <div className="flex flex-wrap gap-3">
              {data.languages.filter(l => l.level).map((lang, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-100 rounded-lg px-4 py-2">
                  <span className="font-medium">{lang.language}</span>
                  <Badge className="bg-rose-500 text-white">{lang.level}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skills */}
        {data.skills?.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 border-b-2 border-rose-400 pb-2 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-rose-500" /> Beceriler
            </h2>
            <div className="flex flex-wrap gap-2">
              {data.skills.map((skill, i) => (
                <Badge key={i} className="bg-slate-800 text-white px-3 py-1">{skill}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Certificates */}
        {data.certificates?.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 border-b-2 border-rose-400 pb-2 mb-4">Sertifikalar</h2>
            <ul className="space-y-1">
              {data.certificates.filter(c => c).map((cert, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  {cert}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  const cvTips = [
    { icon: '📸', tip: 'Profesyonel bir fotoğraf kullanın - gülümseyen, bakımlı' },
    { icon: '📏', tip: 'Boy ve kilo bilgilerinizi doğru yazın' },
    { icon: '🌍', tip: 'Dil seviyelerinizi CEFR standardında belirtin (A1-C2)' },
    { icon: '✈️', tip: 'Müşteri hizmetleri deneyimlerinizi öne çıkarın' },
    { icon: '🎓', tip: 'İlk yardım, güvenlik sertifikalarınızı ekleyin' },
    { icon: '📝', tip: 'CV\'nizi 1-2 sayfa ile sınırlı tutun' }
  ];

  const sections = [
    { id: 'personal', label: 'Kişisel Bilgiler', icon: User },
    { id: 'physical', label: 'Fiziksel Özellikler', icon: User },
    { id: 'education', label: 'Eğitim', icon: GraduationCap },
    { id: 'experience', label: 'Deneyim', icon: Briefcase },
    { id: 'languages', label: 'Diller', icon: Languages },
    { id: 'skills', label: 'Beceriler & Sertifikalar', icon: Award }
  ];

  const languageLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Ana Dil'];

  const suggestedSkills = [
    'Müşteri Hizmetleri', 'İletişim', 'Takım Çalışması', 'Problem Çözme',
    'Stres Yönetimi', 'İlk Yardım', 'Güvenlik Prosedürleri', 'Yemek & İçecek Servisi',
    'Çatışma Yönetimi', 'Zaman Yönetimi', 'Microsoft Office', 'Amadeus/Galileo'
  ];

  const handleInputChange = (section, field, value, index = null) => {
    setCvData(prev => {
      if (index !== null) {
        const newArray = [...prev[section]];
        newArray[index] = { ...newArray[index], [field]: value };
        return { ...prev, [section]: newArray };
      }
      if (section === 'personalInfo' || section === 'physicalInfo') {
        return { ...prev, [section]: { ...prev[section], [field]: value } };
      }
      return prev;
    });
  };

  const addArrayItem = (section, template) => {
    setCvData(prev => ({
      ...prev,
      [section]: [...prev[section], template]
    }));
  };

  const removeArrayItem = (section, index) => {
    setCvData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  const toggleSkill = (skill) => {
    setCvData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'personal':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad *</label>
                <Input
                  value={cvData.personalInfo.fullName}
                  onChange={(e) => handleInputChange('personalInfo', 'fullName', e.target.value)}
                  placeholder="Örn: Ayşe Yılmaz"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">E-posta *</label>
                <Input
                  type="email"
                  value={cvData.personalInfo.email}
                  onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                  placeholder="ornek@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefon *</label>
                <Input
                  value={cvData.personalInfo.phone}
                  onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
                  placeholder="+90 5XX XXX XX XX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Doğum Tarihi *</label>
                <Input
                  type="date"
                  value={cvData.personalInfo.birthDate}
                  onChange={(e) => handleInputChange('personalInfo', 'birthDate', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Uyruk</label>
                <Input
                  value={cvData.personalInfo.nationality}
                  onChange={(e) => handleInputChange('personalInfo', 'nationality', e.target.value)}
                  placeholder="T.C."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
                <Input
                  value={cvData.personalInfo.address}
                  onChange={(e) => handleInputChange('personalInfo', 'address', e.target.value)}
                  placeholder="İstanbul, Türkiye"
                />
              </div>
            </div>
          </div>
        );

      case 'physical':
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                ⚠️ Havayolu şirketleri fiziksel özellikleri işe alım sürecinde değerlendirir. 
                Doğru bilgi vermeniz önemlidir.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Boy (cm) *</label>
                <Input
                  type="number"
                  value={cvData.physicalInfo.height}
                  onChange={(e) => handleInputChange('physicalInfo', 'height', e.target.value)}
                  placeholder="165"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kilo (kg)</label>
                <Input
                  type="number"
                  value={cvData.physicalInfo.weight}
                  onChange={(e) => handleInputChange('physicalInfo', 'weight', e.target.value)}
                  placeholder="58"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ayakkabı No</label>
                <Input
                  value={cvData.physicalInfo.shoeSize}
                  onChange={(e) => handleInputChange('physicalInfo', 'shoeSize', e.target.value)}
                  placeholder="38"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Göz Rengi</label>
                <Input
                  value={cvData.physicalInfo.eyeColor}
                  onChange={(e) => handleInputChange('physicalInfo', 'eyeColor', e.target.value)}
                  placeholder="Kahverengi"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Saç Rengi</label>
                <Input
                  value={cvData.physicalInfo.hairColor}
                  onChange={(e) => handleInputChange('physicalInfo', 'hairColor', e.target.value)}
                  placeholder="Siyah"
                />
              </div>
            </div>
          </div>
        );

      case 'education':
        return (
          <div className="space-y-4">
            {cvData.education.map((edu, index) => (
              <Card key={index} className="border-2">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-medium">Eğitim #{index + 1}</span>
                    {index > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => removeArrayItem('education', index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Okul Adı</label>
                      <Input
                        value={edu.school}
                        onChange={(e) => handleInputChange('education', 'school', e.target.value, index)}
                        placeholder="İstanbul Üniversitesi"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Derece</label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={edu.degree}
                        onChange={(e) => handleInputChange('education', 'degree', e.target.value, index)}
                      >
                        <option value="">Seçiniz</option>
                        <option value="Lise">Lise</option>
                        <option value="Ön Lisans">Ön Lisans</option>
                        <option value="Lisans">Lisans</option>
                        <option value="Yüksek Lisans">Yüksek Lisans</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bölüm</label>
                      <Input
                        value={edu.field}
                        onChange={(e) => handleInputChange('education', 'field', e.target.value, index)}
                        placeholder="Sivil Havacılık"
                      />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç</label>
                        <Input
                          value={edu.startYear}
                          onChange={(e) => handleInputChange('education', 'startYear', e.target.value, index)}
                          placeholder="2018"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş</label>
                        <Input
                          value={edu.endYear}
                          onChange={(e) => handleInputChange('education', 'endYear', e.target.value, index)}
                          placeholder="2022"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button
              variant="outline"
              onClick={() => addArrayItem('education', { school: '', degree: '', field: '', startYear: '', endYear: '' })}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" /> Eğitim Ekle
            </Button>
          </div>
        );

      case 'experience':
        return (
          <div className="space-y-4">
            {cvData.experience.map((exp, index) => (
              <Card key={index} className="border-2">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-medium">Deneyim #{index + 1}</span>
                    {index > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => removeArrayItem('experience', index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Şirket</label>
                      <Input
                        value={exp.company}
                        onChange={(e) => handleInputChange('experience', 'company', e.target.value, index)}
                        placeholder="THY, Hilton..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pozisyon</label>
                      <Input
                        value={exp.position}
                        onChange={(e) => handleInputChange('experience', 'position', e.target.value, index)}
                        placeholder="Misafir İlişkileri Uzmanı"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç</label>
                      <Input
                        type="month"
                        value={exp.startDate}
                        onChange={(e) => handleInputChange('experience', 'startDate', e.target.value, index)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş</label>
                      <Input
                        type="month"
                        value={exp.endDate}
                        onChange={(e) => handleInputChange('experience', 'endDate', e.target.value, index)}
                        placeholder="Devam ediyorsa boş bırakın"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Görev Tanımı</label>
                      <textarea
                        className="w-full border rounded-md p-2 min-h-[80px]"
                        value={exp.description}
                        onChange={(e) => handleInputChange('experience', 'description', e.target.value, index)}
                        placeholder="Müşteri karşılama, şikayet yönetimi..."
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button
              variant="outline"
              onClick={() => addArrayItem('experience', { company: '', position: '', startDate: '', endDate: '', description: '' })}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" /> Deneyim Ekle
            </Button>
          </div>
        );

      case 'languages':
        return (
          <div className="space-y-4">
            {cvData.languages.map((lang, index) => (
              <div key={index} className="flex gap-4 items-center">
                <Input
                  className="flex-1"
                  value={lang.language}
                  onChange={(e) => handleInputChange('languages', 'language', e.target.value, index)}
                  placeholder="Dil"
                />
                <select
                  className="flex-1 border rounded-md p-2"
                  value={lang.level}
                  onChange={(e) => handleInputChange('languages', 'level', e.target.value, index)}
                >
                  <option value="">Seviye Seçin</option>
                  {languageLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
                {index > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem('languages', index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              variant="outline"
              onClick={() => addArrayItem('languages', { language: '', level: '' })}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" /> Dil Ekle
            </Button>
          </div>
        );

      case 'skills':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="font-medium mb-3">Önerilen Beceriler (Seçmek için tıklayın)</h4>
              <div className="flex flex-wrap gap-2">
                {suggestedSkills.map(skill => (
                  <Badge
                    key={skill}
                    variant={cvData.skills.includes(skill) ? 'default' : 'outline'}
                    className={`cursor-pointer ${cvData.skills.includes(skill) ? 'bg-sky-500' : ''}`}
                    onClick={() => toggleSkill(skill)}
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Sertifikalar</h4>
              {cvData.certificates.map((cert, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <Input
                    value={cert}
                    onChange={(e) => {
                      const newCerts = [...cvData.certificates];
                      newCerts[index] = e.target.value;
                      setCvData(prev => ({ ...prev, certificates: newCerts }));
                    }}
                    placeholder="İlk Yardım Sertifikası"
                  />
                  <Button variant="ghost" size="sm" onClick={() => {
                    setCvData(prev => ({
                      ...prev,
                      certificates: prev.certificates.filter((_, i) => i !== index)
                    }));
                  }}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() => setCvData(prev => ({ ...prev, certificates: [...prev.certificates, ''] }))}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" /> Sertifika Ekle
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 p-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-black text-gray-800 flex items-center gap-3">
                <FileText className="w-8 h-8 text-rose-500" />
                Profesyonel CV Hazırlama
              </h1>
              <p className="text-gray-600">Kabin ekibi pozisyonları için özel CV şablonu</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSampleCV(true)} className="border-emerald-300 text-emerald-600 hover:bg-emerald-50">
                <Eye className="w-4 h-4 mr-2" />
                Örnek CV Göster
              </Button>
              <Button variant="outline" onClick={() => setPreviewMode(!previewMode)}>
                <Eye className="w-4 h-4 mr-2" />
                {previewMode ? 'Düzenle' : 'Önizle'}
              </Button>
              <Button className="bg-gradient-to-r from-rose-500 to-pink-600">
                <Download className="w-4 h-4 mr-2" />
                PDF İndir
              </Button>
            </div>
          </div>

          {/* Örnek CV Modal */}
          {showSampleCV && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="relative w-full max-w-4xl my-8">
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute -top-2 -right-2 bg-white rounded-full shadow-lg z-10 hover:bg-gray-100"
                  onClick={() => setShowSampleCV(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
                <div className="bg-emerald-100 text-emerald-800 text-center py-2 rounded-t-xl font-semibold">
                  ✨ Örnek Kabin Memuru CV'si - Bu şekilde görünecek!
                </div>
                <CVPreview data={sampleCVData} isSample={true} />
                <div className="bg-white text-center py-4 rounded-b-xl">
                  <Button onClick={() => setShowSampleCV(false)} className="bg-rose-500 hover:bg-rose-600">
                    Anladım, Kendi CV'mi Hazırlayacağım
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Preview Mode */}
          {previewMode && (
            <div className="mb-8">
              <CVPreview data={cvData} />
            </div>
          )}

          {!previewMode && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Tips */}
            <Card className="lg:col-span-1 h-fit">
              <CardHeader>
                <CardTitle className="text-lg">💡 CV İpuçları</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {cvTips.map((item, index) => (
                  <div key={index} className="flex items-start gap-2 text-sm">
                    <span>{item.icon}</span>
                    <span className="text-gray-600">{item.tip}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* CV Form */}
            <Card className="lg:col-span-3">
              <CardContent className="p-6">
                {/* Section Tabs */}
                <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b">
                  {sections.map(section => (
                    <Button
                      key={section.id}
                      variant={activeSection === section.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setActiveSection(section.id)}
                      className={activeSection === section.id ? 'bg-rose-500' : ''}
                    >
                      <section.icon className="w-4 h-4 mr-1" />
                      {section.label}
                    </Button>
                  ))}
                </div>

                {/* Section Content */}
                {renderSection()}

                {/* Navigation */}
                <div className="flex justify-between mt-8 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const currentIndex = sections.findIndex(s => s.id === activeSection);
                      if (currentIndex > 0) setActiveSection(sections[currentIndex - 1].id);
                    }}
                    disabled={activeSection === sections[0].id}
                  >
                    ← Önceki
                  </Button>
                  {activeSection === sections[sections.length - 1].id ? (
                    <Button
                      onClick={() => setPreviewMode(true)}
                      className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      CV'yi Önizle ✓
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        const currentIndex = sections.findIndex(s => s.id === activeSection);
                        if (currentIndex < sections.length - 1) setActiveSection(sections[currentIndex + 1].id);
                      }}
                      className="bg-rose-500 hover:bg-rose-600"
                    >
                      Sonraki →
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          )}
        </div>
      </div>
    </CabinCrewLayout>
  );
}
